import { useNavigate } from "react-router-dom";
import { useDialogContext } from "@/components/DialogProvider";
import { useRoute, type RouteId } from "@/routes/getRoute";
import {
  FEATURE_PROTECTED_ROUTES,
  PARTNERSHIP_PROTECTED_ROUTES,
} from "@/routes/protectedRoutes";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { UrlQueryObject } from "@/lib/UrlSerializers";
import { AnalyticsEvent, useAnalytics } from "@/hooks/useAnalytics";

/**
 * Hook that provides protected navigation with automatic feature flag checking
 */
export const useProtectedNavigation = () => {
  const navigate = useNavigate();
  const getRoute = useRoute();
  const { showFeatureDialog, showPermissionDialog } = useDialogContext();
  const { hasOrgFeature, hasMinOrgPartnershipLevel } = useOrgPermissions();
  const { trackEvent } = useAnalytics();

  /**
   * Navigate to a route with automatic feature flag checking
   * @param routeId - The route identifier
   * @param params - Route parameters
   * @param query - Query string parameters
   * @param options - Navigation options including analytics tracking
   */
  const navToRoute = (
    routeId: RouteId,
    params: Record<string, string> = {},
    query?: UrlQueryObject,
    options?: {
      preventAutoScroll?: boolean;
      hash?: string;
      newTab?: boolean;
      replace?: boolean;
      /** Analytics event name to track on navigation */
      analyticsEvent?: AnalyticsEvent;
      /** Analytics properties to include with the event */
      analyticsProperties?: Record<string, unknown>;
    },
  ) => {
    const partnerFeature = FEATURE_PROTECTED_ROUTES[routeId];
    const requiredPartnershipLevel = PARTNERSHIP_PROTECTED_ROUTES[routeId];

    if (partnerFeature) {
      const hasFeature = hasOrgFeature(partnerFeature);

      if (!hasFeature) {
        showFeatureDialog(partnerFeature);
        return;
      }
    }

    if (requiredPartnershipLevel) {
      const hasLevel = hasMinOrgPartnershipLevel(requiredPartnershipLevel);

      if (!hasLevel) {
        showPermissionDialog();
        return;
      }
    }

    // Track analytics event if provided
    if (options?.analyticsEvent) {
      trackEvent(options.analyticsEvent, options.analyticsProperties);
    }

    let route = getRoute(routeId, params, query);

    if (options?.hash) {
      route += `#${options.hash}`;
    }

    if (options?.newTab) {
      window.open(route, "_blank");
    } else if (options?.preventAutoScroll || options?.replace) {
      navigate(route, {
        ...(options?.preventAutoScroll && {
          state: { preventAutoScroll: true },
        }),
        ...(options?.replace && { replace: true }),
      });
    } else {
      navigate(route);
    }
  };

  const canRoute = (routeId: RouteId): boolean => {
    const partnerFeature = FEATURE_PROTECTED_ROUTES[routeId];
    const requiredPartnershipLevel = PARTNERSHIP_PROTECTED_ROUTES[routeId];

    if (partnerFeature) {
      const hasFeature = hasOrgFeature(partnerFeature);
      if (hasFeature !== true) {
        return false;
      }
    }

    if (requiredPartnershipLevel) {
      const hasLevel = hasMinOrgPartnershipLevel(requiredPartnershipLevel);
      if (hasLevel !== true) {
        return false;
      }
    }

    return true;
  };

  return { navToRoute, canRoute };
};
